/**
 * Era scenes — "historical scenes" of a main scene (e.g. how the Kaaba looked
 * across centuries). Reached via the top-right clock button → centered slider,
 * and via the menu's «Historical Scenes» tab — not via POI markers.
 *
 * Architecturally identical to inside-scenes: ordinary `SceneConfig`s that go
 * through the standard `SceneCanvas` pipeline (preloader, reveal, dispose,
 * HUD). `splatRegistry.ts` falls back here, so `isSplatScene(eraId)` and
 * `getSplatSceneConfig(eraId)` return valid values. They are children of their
 * main scene on the stack (`[kaaba, era]`) — the future "Back to {parent}"
 * button (deferred) will `goBack()` to the parent, exactly like inside-scenes.
 *
 * Every era registered here is a REAL scene: its own splat assets + preview GLB
 * and full chat/POI support (see the config files next to this one +
 * `BACKEND_ERA_SCENE_IDS` below). A future era with no assets yet goes in as a
 * config that stays OUT of `BACKEND_ERA_SCENE_IDS` — `isStubEraScene` then locks
 * its card ("Coming soon") everywhere.
 *
 * The display list (order + labels + previews) lives on the MAIN scene config
 * as `historicalScenes` (see `kaaba.ts`), including the "today" card that maps
 * back to the live scene. This registry only owns the era SCENE configs +
 * parent map. Family resolution (id → slides) lives in
 * `src/state/historicalScenes.ts` to avoid an import cycle with `splatRegistry`.
 */
import type { SceneConfig } from '../sceneConfig';
import { kaaba2000BceSceneConfig } from './kaaba2000Bce';
import { kaaba605SceneConfig } from './kaaba605';
import { kaabaOttomanSceneConfig } from './kaabaOttoman';
import { kaabaFutureSceneConfig } from './kaabaFuture';

const eraSceneConfigs: Record<string, SceneConfig> = {
  'kaaba-2000bce': kaaba2000BceSceneConfig,
  'kaaba-600ce': kaaba605SceneConfig,
  'kaaba-1890s': kaabaOttomanSceneConfig,
  'kaaba-2030': kaabaFutureSceneConfig,
};

/**
 * Which main scene an era belongs to (back-navigation target + family lookup).
 * Mirrors `INSIDE_SCENE_PARENT`.
 */
export const ERA_SCENE_PARENT: Record<string, string> = {
  'kaaba-2000bce': 'kaaba',
  'kaaba-600ce': 'kaaba',
  'kaaba-1890s': 'kaaba',
  'kaaba-2030': 'kaaba',
};

/**
 * Era scenes the backend supports as FULL scenes (chat + POI via `scene_open`),
 * as opposed to visual stubs. Their poiFilterId is added to the `scene_open`
 * whitelist (`SCENE_OPEN_SUPPORTED_SCENE_IDS` in `state/sceneOpen.ts`) so the
 * client requests an intro card + `tour`. Stub eras stay OUT of this set — they
 * must not fire `scene_open` (the backend has no data for them).
 */
export const BACKEND_ERA_SCENE_IDS: ReadonlySet<string> = new Set([
  'kaaba-2000bce',
  'kaaba-600ce',
  'kaaba-1890s',
  'kaaba-2030',
]);

export const getEraSceneConfig = (id: string): SceneConfig | undefined =>
  eraSceneConfigs[id];

export const isEraScene = (id: string): boolean => id in eraSceneConfigs;

/**
 * Stub era: registered for the slider but with no backend chat/POI — i.e. the
 * scene isn't really built yet. None exist right now. Derived from
 * `BACKEND_ERA_SCENE_IDS` so there's no second id list to keep in sync; adding
 * an era there flips it to "real" everywhere (slider card unlocks, analytics
 * stops tagging it as a stub).
 */
export const isStubEraScene = (id: string): boolean =>
  isEraScene(id) && !BACKEND_ERA_SCENE_IDS.has(id);

export const getEraSceneIds = (): string[] => Object.keys(eraSceneConfigs);
